import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const DirectBookingsTest = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchBookings();
    fetchStats();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${apiUrl}/api/diagnostic/bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Direct bookings API response:', response.data);
      
      if (response.data.success) {
        setBookings(response.data.data || []);
      } else {
        setError('Failed to fetch bookings: ' + response.data.message);
        toast.error('Failed to fetch bookings');
      }
    } catch (error) {
      console.error('Error fetching bookings directly:', error);
      setError('Error fetching bookings: ' + error.message);
      toast.error(`Failed to load bookings: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${apiUrl}/api/diagnostic/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Database stats API response:', response.data);
      
      if (response.data.success) {
        setStats(response.data.data || null);
      }
    } catch (error) {
      console.error('Error fetching database stats:', error);
    }
  };

  const renderPaymentMethodBadge = (paymentMethod) => {
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    let displayText = paymentMethod || 'Unknown';
    
    if (paymentMethod === 'pay_later') {
      bgColor = 'bg-purple-100';
      textColor = 'text-purple-800';
      displayText = 'Pay Later';
    } else if (paymentMethod === 'credit_card') {
      bgColor = 'bg-blue-100';
      textColor = 'text-blue-800';
      displayText = 'Credit Card';
    } else if (paymentMethod === 'bank_transfer') {
      bgColor = 'bg-green-100';
      textColor = 'text-green-800';
      displayText = 'Bank Transfer';
    }
    
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${bgColor} ${textColor}`}>
        {displayText}
      </span>
    );
  };

  const renderStatusBadge = (status, paymentStatus, paymentMethod) => {
    // Check if it's a Pay Later booking with pending payment
    const isPendingPayLater = 
      (paymentStatus === 'pending' || paymentStatus === 'awaiting_office_payment') && 
      paymentMethod === 'pay_later';
    
    if (isPendingPayLater) {
      return (
        <div>
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
            Pay Later
          </span>
          <p className="text-xs text-gray-500 mt-1">Pending Payment</p>
        </div>
      );
    }
    
    // Other status badges
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    let displayText = status || 'Unknown';
    
    if (paymentStatus === 'paid') {
      bgColor = 'bg-green-100';
      textColor = 'text-green-800';
      displayText = 'Paid';
    } else if (paymentStatus === 'pending') {
      bgColor = 'bg-yellow-100';
      textColor = 'text-yellow-800';
      displayText = 'Pending';
    } else if (status === 'confirmed') {
      bgColor = 'bg-blue-100';
      textColor = 'text-blue-800';
      displayText = 'Confirmed';
    } else if (status === 'cancelled') {
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
      displayText = 'Cancelled';
    }
    
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${bgColor} ${textColor}`}>
        {displayText}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Direct Database Bookings Test</h1>
        <button
          onClick={fetchBookings}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          Refresh
        </button>
      </div>
      
      {/* Database Stats */}
      {stats && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Database Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="font-medium">Bookings</h3>
              <p className="text-2xl font-bold">{stats.bookings.total}</p>
              <div className="mt-2">
                <h4 className="text-sm font-medium">By Status:</h4>
                <ul className="text-sm">
                  {stats.bookings.by_status.map((item, index) => (
                    <li key={index}>{item.status}: {item.count}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-2">
                <h4 className="text-sm font-medium">By Payment Method:</h4>
                <ul className="text-sm">
                  {stats.bookings.by_payment_method.map((item, index) => (
                    <li key={index}>{item.payment_method || 'None'}: {item.count}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="font-medium">Tours</h3>
              <p className="text-2xl font-bold">{stats.tours.total}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="font-medium">Users</h3>
              <p className="text-2xl font-bold">{stats.users.total}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Bookings Table */}
      <div className="bg-white shadow rounded-lg">
        {loading ? (
          <div className="animate-pulse p-6">
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <div className="text-red-500 mb-4">{error}</div>
            <button
              onClick={fetchBookings}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Retry
            </button>
          </div>
        ) : bookings.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">No bookings found in the database.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Booking
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tour
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Travelers
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Method
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-primary">
                        {booking.booking_number || `BK-${booking.id}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {booking.user?.name || (booking.customer_details?.name) || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {booking.user?.email || (booking.customer_details?.email) || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{booking.tour?.name || 'N/A'}</div>
                      <div className="text-sm text-gray-500">
                        {booking.tour?.location || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {booking.travel_date ? new Date(booking.travel_date).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(booking.adults || 0) + (booking.children || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ${booking.total_amount ? parseFloat(booking.total_amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderPaymentMethodBadge(booking.payment_method)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderStatusBadge(booking.status, booking.payment_status, booking.payment_method)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Raw Data Display */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Raw Booking Data</h2>
        <pre className="bg-gray-100 p-4 rounded-md overflow-auto text-xs max-h-96">
          {JSON.stringify(bookings, null, 2)}
        </pre>
      </div>
    </div>
  );
};

export default DirectBookingsTest;
